import { JsonPipe } from '@angular/common';
import { Component, ElementRef, Inject, Input, OnInit, OnDestroy, Sanitizer, ViewChild, AfterViewInit } from '@angular/core';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom, forkJoin } from 'rxjs';
import { EProgressionPaquet } from 'src/enum/eProgressionPaquet';
import { EvenementModel } from 'src/models/evenement-model';
import { EvenementSessionScormModel } from 'src/models/evenement-session-scorm-model';
import { ReferenceDocumentScormModel, ScormPackageLocaleModel, ScormPackageModel } from 'src/models/scormPackageModel';
import { ScormPackageProgressionModel } from 'src/models/scormPackageProgressionModel';
import { AttachedDocumentModel } from 'src/models/attachedDocumentModel';
import { AuthService } from 'src/services/auth.service';
import { ContenuSessionService } from 'src/services/contenu.session.service';
import { ScormService, SCORM_SESSION_STORAGE_KEY } from 'src/services/scorm.service';
import { UtilisateurService } from 'src/services/utilisateur.service';
import { PreviewConfirmationDialogComponent } from '../preview-confirmation-dialog/preview-confirmation-dialog.component';
import { SanitizeHtmlPipe } from 'src/app/pipe/sanitize-html.pipe';

export interface ScormDescriptionDialogData {
  scormPackage: ScormPackageProgressionModel;
  estDemo: boolean;
  formationGroupeId?: number | null;
}

@Component({
    selector: 'app-scorm-description-dialog',
    templateUrl: './scorm-description-dialog.component.html',
    styleUrls: ['./scorm-description-dialog.component.scss'],
    imports: [
    MatIconModule,
    TranslateModule,
    MatProgressSpinnerModule,
    SanitizeHtmlPipe
]
})
export class ScormDescriptionDialogComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('startButton') startButton!: ElementRef<HTMLButtonElement>;
  @ViewChild('descriptionContainer') descriptionContainer!: ElementRef<HTMLDivElement>;

  isLoading = false;
  scormPackage!: ScormPackageProgressionModel;
  scormSessionProgression!: number;
  scormConfig!: any;
  dataSession: EvenementSessionScormModel[] = [];
  estDemo!: boolean;
  eProgressionPaquet = EProgressionPaquet;
  references: ReferenceDocumentScormModel[] = [];
  attachedDocuments: AttachedDocumentModel[] = [];
  private descriptionClickListener: ((event: Event) => void) | null = null;

  constructor(
    public dialogRef: MatDialogRef<ScormDescriptionDialogComponent>,
    private scormService: ScormService,
    private utilisateurService: UtilisateurService,
    private authService: AuthService,
    private contenuSessionService: ContenuSessionService,
    @Inject(MAT_DIALOG_DATA) public data: ScormDescriptionDialogData,
    public translate: TranslateService,
    private router: Router,
    private dialog: MatDialog,
    private elementRef: ElementRef,
    private snackBar: MatSnackBar
  ) {
    this.scormPackage = data.scormPackage;
    this.estDemo = data.estDemo;
  }

  ngOnInit() {
    this.chargerData();
  }

  private chargerData() {
    this.isLoading = true;
    const requests$ = forkJoin({
      config: this.scormService.getScormConfig(this.data.scormPackage.scormPackageId),
      progression: this.contenuSessionService.ObtenirProgressionPackage(this.data.scormPackage.scormPackageId, this.data.formationGroupeId ?? null),
      attachedDocuments: this.scormService.getAttachedDocuments(this.data.scormPackage.scormPackageId)
    });

    requests$.subscribe({
      next: (result: any) => {
        this.scormConfig = result.config;
        this.references = Object.assign([], result.config?.references || []);
        this.attachedDocuments = this.references.length === 0 ? (result.attachedDocuments || []) : [];
        window.configScorm = result.config;
        this.scormPackage.formationGroupeId = this.data.formationGroupeId;
        this.scormPackage.estFormationGroupe = this.scormPackage.formationGroupeId != null && this.scormPackage.formationGroupeId == 1;

        if (result.progression) {
          this.scormSessionProgression = result.progression.eProgression;
          this.scormPackage.progression = this.scormSessionProgression;
          this.scormPackage.sessionId = result.progression.sessionId;
        }

        this.isLoading = false;
        setTimeout(() => {
          this.attachDescriptionClickListener();
        }, 100);
      },
      error: (err: any) => {
        console.error("Erreur lors du chargement", err);
        this.scormConfig = { references: [], modules: {} };
        this.references = [];
        this.attachedDocuments = [];
        window.configScorm = this.scormConfig;
        this.isLoading = false;
      }
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.attachDescriptionClickListener();
    }, 0);
  }

  private attachDescriptionClickListener(): void {
    if (this.descriptionContainer) {
      const descriptionDiv = this.descriptionContainer.nativeElement;

      this.descriptionClickListener = (event: Event) => {
        const mouseEvent = event as MouseEvent;
        let target = mouseEvent.target as HTMLElement;

        while (target && target !== descriptionDiv) {
          if (target.tagName === 'A' && target.classList.contains('link-href')) {
            mouseEvent.preventDefault();
            const nomPackage = target.getAttribute('data-name-package') || '';
            if (nomPackage !== '') {
              this.obtenirPackageFormation(nomPackage);
            }
            return;
          }
          target = target.parentElement as HTMLElement;
        }
      };

      descriptionDiv.addEventListener('click', this.descriptionClickListener);
    }
  }

  ngOnDestroy() {
    if (this.descriptionContainer && this.descriptionClickListener) {
      this.descriptionContainer.nativeElement.removeEventListener('click', this.descriptionClickListener);
    }
  }

  obtenirPackageFormation(nomPackage: string): void {
    this.isLoading = true;
    const scormPackage = this.scormService.scormPackages.filter(d => d.nomPackage == nomPackage)[0] as  ScormPackageProgressionModel;
    this.scormService.obtenirProgressionPackage(scormPackage.scormPackageId).subscribe({
      next: (reponse: EProgressionPaquet) => {
              this.scormPackage = scormPackage;
              this.scormPackage.progression = reponse;
              this.isLoading = false;
            },
            error: (err) => {
            this.isLoading = false;
         }
      });
    };

  private async getDataSession(sessionId: number): Promise<void> {
    console.log("getDataSession: Début avec sessionId =", sessionId);
    if (this.scormPackage.progression === EProgressionPaquet.EnCours) {
      try {
        const reponse = await firstValueFrom(
          this.contenuSessionService.obtenirDonneesSession(sessionId!)
        );
        console.log("getDataSession: Réponse de obtenirDonneesSession =", reponse);

        window.dataSession = reponse;
        window.sessionId = sessionId;

      } catch (error) {
        console.error("Erreur lors de la récupération des données de session :", error);
        throw error;
      }
    } else {
      console.log("getDataSession: Progression n'est pas EnCours, réinitialisation de window.dataSession");
      window.dataSession = [];
    }

  }

  private async CreerObtenirSession(): Promise<void> {
    console.log("CreerObtenirSession: Début de la création de session");
    try {
      let evenementModel: EvenementModel = new EvenementModel();
      evenementModel.scormContentId = this.scormPackage.scormPackageId;
      evenementModel.ssoId = this.authService.User?.profile.sub ?? '';
      evenementModel.formationGroupId = this.scormPackage.formationGroupeId;
      evenementModel.entiteId = this.utilisateurService.entiteUtilisateurSelectionnee.entiteId;
      evenementModel.utilisateurId = this.utilisateurService.entiteUtilisateurSelectionnee.utilisateurId;
      evenementModel.typeEvenements = [];
      evenementModel.typeEvenements.push({ element: "LMSInitialize", value: "initialize" });

      const languageToUse = window.currentLanguage || this.translate.currentLang || "fr";
      evenementModel.typeEvenements.push({ element: "cmi.learner_preference.language", value: languageToUse });

      console.log("CreerObtenirSession: EvenementModel préparé =", evenementModel);

      this.isLoading = true;
      const reponse = await firstValueFrom(this.contenuSessionService.creerSession(evenementModel));
      this.isLoading = false;
      console.log("CreerObtenirSession: Réponse de creerSession =", reponse);
      window.dataSession = reponse;

      if (window.dataSession && window.dataSession.length > 0) {
        window.sessionId = window.dataSession[0].sessionId;
        console.log("CreerObtenirSession: window.sessionId défini à =", window.sessionId);
      } else {
        console.warn("CreerObtenirSession: window.dataSession est vide ou nul après création");
      }

    } catch (error) {
      console.error("Erreur lors de la création de la session :", error);
      throw error;
    }
  }

  async startScorm(scormPackageId: number, sessionId?: number | null) {
    console.log("Démarrage du SCORM avec scormPackageId :", scormPackageId, "et sessionId :", sessionId);
    try {
      if (!await this.ensureVideosReady(scormPackageId)) {
        return;
      }

      if (this.scormPackage.progression === EProgressionPaquet.EnCours) {
        console.log("startScorm: Progression EnCours, appel de getDataSession");
        await this.getDataSession(sessionId!);
      } else {
        console.log("startScorm: Progression non commencée ou terminée, appel de CreerObtenirSession");
        await this.CreerObtenirSession();
      }

      console.log("startScorm: Session initialisée. window.sessionId =", window.sessionId, "window.dataSession =", window.dataSession);

      this.persistSessionState(scormPackageId);

      this.dialogRef.close(this.scormPackage);

      if (this.scormPackage.estFormationGroupe) {
        console.log("startScorm: Navigation vers formationGroupe");
        this.router.navigate(['scormviewer', scormPackageId, 'formationGroupe', this.scormPackage.formationGroupeId
        ]);
      } else {
        console.log("startScorm: Navigation vers scormviewer standard");
        this.router.navigate(['scormviewer', scormPackageId]);
      }
    } catch (error) {
      console.error("Impossible de démarrer le SCORM :", error);
    }
  }

  private async ensureVideosReady(scormPackageId: number): Promise<boolean> {
    try {
      const result = await firstValueFrom(this.scormService.getPackageVideosStatus(scormPackageId));

      if (result.status === 'ready') {
        return true;
      }

      const messageKey = result.status === 'error'
        ? 'SCORMS_LIST.VIDEO_NOT_AVAILABLE'
        : 'SCORMS_LIST.VIDEO_PROCESSING';

      this.snackBar.open(this.translate.instant(messageKey), 'Fermer', { duration: 6000 });
      return false;
    } catch (error) {
      console.error("Impossible de vérifier le statut des vidéos :", error);
      return true;
    }
  }

  private persistSessionState(scormPackageId: number): void {
    if (window.sessionId && window.sessionId > 0) {
      sessionStorage.setItem(
        SCORM_SESSION_STORAGE_KEY,
        JSON.stringify({ scormId: scormPackageId, sessionId: window.sessionId })
      );
    }
  }

  previewScorm(scormPackageId: number) {
    const confirmDialogRef = this.dialog.open(PreviewConfirmationDialogComponent, {
      width: '500px',
      disableClose: false
    });

    confirmDialogRef.afterClosed().subscribe(result => {
      if (result === true) {
        sessionStorage.removeItem(SCORM_SESSION_STORAGE_KEY);
        window.dataSession = [];
        window.sessionId = 0;

        this.dialogRef.close(this.scormPackage);

        this.router.navigate(['scormviewer', scormPackageId], {
          queryParams: { preview: 'true' }
        });
      }
    });
  }

  getLocalizedContent(locales: ScormPackageLocaleModel[]): ScormPackageLocaleModel {
    const currentLang = this.translate.currentLang || 'fr';
    return locales.find(locale => locale.langue.toLowerCase() === currentLang) || locales[0];
  }

  getdurationInMinutes(): any {
    const currentLang = this.translate.currentLang || 'fr';
    return this.scormPackage.locales.find(locale => locale.langue.toLowerCase() === currentLang)?.dureeEnMinutes;
  }

  getLocalizedDescription(): any {
    const currentLang = this.translate.currentLang || 'fr';
    return this.scormPackage.locales.find(locale => locale.langue.toLowerCase() === currentLang)?.description;
  }
  getsubtitle(): any {
    const currentLang = this.translate.currentLang || 'fr';
    return this.scormPackage.locales.find(locale => locale.langue.toLowerCase() === currentLang)?.configSousTitre;
  }

  getLocalizedEvaluationCritere(): any {
    const currentLang = this.translate.currentLang || 'fr';
    return this.scormPackage.locales.find(locale => locale.langue.toLowerCase() === currentLang)?.critereEvaluation;
  }

  getLocalizedHrefDocumentrefrenceContent(reference: any): string {
    const currentLang = this.translate.currentLang || 'fr';
    if (reference.href[currentLang]) {
      return reference.href[currentLang];
    }
    return reference.href['fr'];
  }

  getLocalizedDocumentreferenceContent(reference: any): string {
    const currentLang = this.translate.currentLang || 'fr';
    if (reference.name[currentLang]) {
      return reference.name[currentLang];
    }
    return reference.name['fr'];
  }

  isAccesDownloadCertificat(): void {
    if (this.estDemo) {
      return;
    }

    if (this.scormPackage.progression === EProgressionPaquet.Complete) {
      this.contenuSessionService.obtenirInfosCertificat(this.scormPackage.scormPackageId, this.translate.currentLang || 'fr').subscribe(res => {
        this.contenuSessionService.imprimerCertificatUtilisateur([res], this.translate.currentLang || 'fr').subscribe({
          next: ({ blob, contentType }) => {
            this.ouvrirDocumentOnglet(blob, contentType!);
          }
        })
      });
    } else {
      this.startButton.nativeElement.focus();
    }
  }

  downloadRapportFormation(scormPackageId: number, reference: ReferenceDocumentScormModel) {
    const nomDocument = this.getLocalizedHrefDocumentrefrenceContent(reference);
    this.contenuSessionService.telechargerDocumentFormation(scormPackageId, this.scormPackage.nomPackage, nomDocument).subscribe(({ blob, contentType }) => {
      this.ouvrirDocumentOnglet(blob, contentType!);
    })
  }

  downloadAttachedDocument(document: AttachedDocumentModel) {
    const nomDocument = `references/${document.nomFichier}`;
    this.contenuSessionService.telechargerDocumentFormation(this.scormPackage.scormPackageId, this.scormPackage.nomPackage, nomDocument).subscribe(({ blob, contentType }) => {
      this.ouvrirDocumentOnglet(blob, contentType!);
    });
  }

  getAttachedDocumentLabel(document: AttachedDocumentModel): string {
    return document.titre && document.titre.trim().length > 0 ? document.titre : document.nomFichier;
  }

  getAttachedDocumentIcon(document: AttachedDocumentModel): string {
    const fileName = document.nomFichier || '';
    const dotIndex = fileName.lastIndexOf('.');
    const extension = dotIndex >= 0 ? fileName.substring(dotIndex + 1).toLowerCase() : '';

    switch (extension) {
      case 'pdf':
        return 'picture_as_pdf';
      case 'doc':
      case 'docx':
      case 'rtf':
      case 'odt':
      case 'txt':
        return 'description';
      case 'xls':
      case 'xlsx':
      case 'csv':
      case 'ods':
        return 'table_view';
      case 'ppt':
      case 'pptx':
      case 'odp':
        return 'slideshow';
      case 'png':
      case 'jpg':
      case 'jpeg':
      case 'gif':
      case 'bmp':
      case 'webp':
      case 'svg':
        return 'image';
      case 'mp4':
      case 'mov':
      case 'avi':
      case 'mkv':
      case 'webm':
        return 'movie';
      case 'mp3':
      case 'wav':
      case 'ogg':
      case 'm4a':
        return 'audiotrack';
      case 'zip':
      case 'rar':
      case '7z':
      case 'tar':
      case 'gz':
        return 'folder_zip';
      default:
        return 'insert_drive_file';
    }
  }

  ouvrirDocumentOnglet(reponse: Blob, type: string) {
    var newBlob: Blob;
    newBlob = new Blob([reponse], { type: type });
    const fileURL = URL.createObjectURL(newBlob);
    window.open(fileURL, '_blank');
  }

}
